import isWebView from 'is-ua-webview';
import qs from 'qs';
import { UiResponse } from '../CommonInterfaces';
import config from '../Config';

const forwardParamsNames = [
  'authlevel',
  'teliaLogin',
  'app',
  'goto',
  'gotoOnFail',
  'selectMandate',
  'ForceAuth',
  'service',
  'locale',
  'lang',
  'loginURI',
  'webapi',
  'panelFilter',
  'hideB2B',
  'hideLogout',
  'autoSelectSelf',
  'rememberMe',
  'RequestID',
  'IssueInstant',
  'ProviderID',
  'RelayState',
  'cdcRequested',
  'initialRequestUri',
  'allowStatisticsCookies',
  'hideRememberMe',
  'deeplink',
  'forceSelectCustomerId'
];

interface Parameters {
  [x: string]: any;
}

export const filterForwardParamsFromCurrentParams = (currentParams: Parameters, exclude?: string[]): string => {
  return forwardParamsQueryString(filterForwardParamsFromCurrentParamsReturningParameters(currentParams, exclude));
};

export const filterForwardParamsFromCurrentParamsReturningParameters = (currentParams: Parameters, exclude?: string[]): Parameters => {
  return filterForwardParams(currentParams, defaultParamsFunction, exclude);
};

export const filterForwardParamsForStartExternalAuth = (currentParams: Parameters): string => {
  return forwardParamsQueryString(filterForwardParams(currentParams, startExternalAuthParamsFunction));
};

export const paramsWithAdditionalParams = (currentParams: string, additionalParams: { [key: string]: string }) => {
  const additionalParamsString = Object.entries(additionalParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  return currentParams ? `${currentParams}&${additionalParamsString}` : `?${additionalParamsString}`;
};

const filterForwardParams = (
  originalParams: Parameters,
  filterFunction: (key: string, orig: Parameters, res: Parameters, exclude?: string[]) => void,
  exclude?: string[]
) => {
  const filteredParams: Parameters = {};
  forwardParamsNames.forEach((param) => filterFunction(param, originalParams, filteredParams, exclude));
  return filteredParams;
};

export const preserveParams = (originalParams: Parameters, preserve?: string[]) => {
  const filteredParams: Parameters = {};
  preserve?.forEach((param) => {
    if (!!originalParams[param] && preserve.includes(param)) {
      filteredParams[param] = originalParams[param];
    }
  });
  return forwardParamsQueryString(filteredParams);
};

const forwardParamsQueryString = (params: Parameters) => {
  return params && Object.keys(params).length ? qs.stringify(params, { addQueryPrefix: true, skipNulls: true }) : '';
};

const defaultParamsFunction = (key: string, originalParams: Parameters, resultParams: Parameters, exclude?: string[]) => {
  if (!!originalParams[key] && !exclude?.includes(key)) {
    resultParams[key] = originalParams[key];
  }
};

const startExternalAuthParamsFunction = (key: string, originalParams: Parameters, resultParams: Parameters) => {
  if (key === 'goto' || !!originalParams[key]) {
    if (key === 'webapi' && !originalParams['deeplink'] && !isWebView(navigator.userAgent)) {
      resultParams[key] = 'false';
    } else if (key === 'goto' && !originalParams[key]) {
      resultParams[key] = resolveGoto();
    } else {
      resultParams[key] = originalParams[key];
    }
  }
};

const resolveGoto = () => {
  return document.referrer || getGotoByLocation();
};

const getGotoByLocation = () => {
  if (document.location.host.includes('sso')) {
    let domainStart;
    switch (config.env) {
      case 'live':
        domainStart = 'www';
        break;
      case 'local':
        domainStart = 'sso-local';
        break;
      default:
        domainStart = `www-${config.env}`;
        break;
    }
    return `https://${domainStart}${window.location.host.substring(window.location.host.indexOf('.'))}`;
  }
  return document.location.href;
};

export const extractQueryStringFromResponse = (data: UiResponse) => {
  return data?.params && Object.keys(data.params).length ? qs.stringify(data.params, { addQueryPrefix: true }) : '';
};
